import React, { useState } from 'react';
import { message } from "antd";
import {
    EditOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../../../lib/query-constants';
import { MSG_LEADSOURCE } from '../../../../utils/message';
import CommonTable from '../../../global/table';
import CommonDialog from '../../../global/dialog';
import NoRecordFound from '../../../global/noRecordFound';
import { useLeadSources } from "../../../../queries/common/use-lead-source"
import CommonCardView from '../../../global/cardView';
import EnhancedLeadSourceGridView from './enhancedLeadSourceGridView';

const EnhanceLeadSourceList = ({
    isLoading,
    jobs,
    getLeadSources,
    sort,
    setSort,
    keyParams,
    setEditLeadSource,
    viewType,
    setViewDetail
}) => {
    const [toggleModal, setToggleModal] = useState({
        show: false,
        type: "",
    });
    const [deleteId, setDeleteId] = useState(null);

    // Fetch lead source options
    const { data: leadSource, isLoading: leadSourceLoading } = useLeadSources({ endpoint: "leadsource/options" });

    // mutations
    const queryClient = useQueryClient()
    const deleteLeadSourceMutation = useGenericMutation({
        endpoint: "leadsource/delete",
        onError: (err) => {
            console.error(err)
            message.error(err.response?.error || "Error deleting lead source.")
        },
        onSuccess: () => {
            setToggleModal({ show: false, type: "" });
            setDeleteId(null);
            message.success("Lead source deleted successfully.")
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.leadSources.lists(keyParams),
                type: "active",
                exact: true
            })
        },
    })

    const getLeadSourceTitle = (leadsourceKey) => {
        if (!leadSource || !Array.isArray(leadSource)) return "";
        const source = leadSource.find((ls) => ls.key === leadsourceKey);
        return source ? source.title : "";
    };

    const handleEdit = async (id) => {
        if (setEditLeadSource) {
            setEditLeadSource({
                show: true,
                isEditable: true,
                id: id
            });
        }
    };

    const removeLeadSource = async () => {
        try {
            const postData = {
                id: deleteId,
            };
            await deleteLeadSourceMutation.mutateAsync(postData)
        } catch (error) {
            message.error("Failed to delete lead source");
        }
    };

    // Columns Configuration
    const columns = [
        {
            title: "Lead Source",
            dataIndex: "leadSource",
            key: "leadSource",
            sortType: "leadSource",
        },
        {
            title: "Job Name",
            dataIndex: "jobName",
            key: "jobName",
            sortType: "jobName",
            render: (text, record) => (
                <a onClick={() => setViewDetail({ show: true, data: record.originalData || record })} className="text-primary cursor-pointer">
                    {text}
                </a>
            ),
        },
        {
            title: "Email/Username",
            dataIndex: "username",
            key: "username",
            sortType: "username",
        },
        {
            title: "Active",
            dataIndex: "active",
            key: "active",
            sortType: "active",
            render: (active) => (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                    {active ? 'Yes' : 'No'}
                </span>
            ),
        },
    ];

    // Data Mapping
    const data = Array.isArray(jobs) && jobs.length > 0
        ? jobs.map((job) => ({
            ...job,
            key: job._id,
            id: job._id,
            leadSource: getLeadSourceTitle(job.leadSource),
            jobName: job.jobName,
            username: job.username || '-',
            active: job.active,
        }))
        : [];

    // Actions Configuration
    const actions = [
        {
            label: "Edit",
            icon: <EditOutlined />,
            onClick: (record) => handleEdit(record.id),
            className: "!text-primary !border-primary hover:!border-primary hover:!bg-primary hover:!text-white",
        },
        {
            label: "Delete",
            icon: <DeleteOutlined />,
            onClick: (record) => {
                setDeleteId(record.id);
                setToggleModal({ show: true, type: "delete" });
            },
            className: "!text-red-500 !border-red-500 hover:!border-red-500 hover:!bg-red-500 hover:!text-white",
        }
    ];

    return (
        <>
            {data.length > 0 ? (
                <>
                    {viewType === "LIST" ? (
                        <CommonTable
                            columns={columns}
                            data={data}
                            showActions={true}
                            actions={actions}
                            sort={sort}
                            setSort={setSort}
                            onRowClick={(record) => setViewDetail({ show: true, data: record.originalData || record })}
                        />
                    ) : (
                        <CommonCardView>
                            {data.map((res) => (
                                <EnhancedLeadSourceGridView
                                    key={res.id}
                                    record={res}
                                    actions={actions}
                                    onCardClick={(record) => setViewDetail({ show: true, data: record.originalData || record })}
                                />
                            ))}
                        </CommonCardView>
                    )}
                </>
            ) : (
                <NoRecordFound message="No lead sources found." />
            )}

            {toggleModal.show && (
                <CommonDialog
                    title="Delete Import Job"
                    description="Are you sure you want to delete this Import Job?"
                    open={toggleModal.show}
                    setOpen={() => setToggleModal({ show: false, type: "" })}
                    onOk={removeLeadSource}
                    onCancel={() => setToggleModal({ show: false, type: "" })}
                    isConfirmButtonLoading={deleteLeadSourceMutation.isPending}
                />
            )}
        </>
    );
};

export default EnhanceLeadSourceList;